import {
  SavedLocation,
  booleanSchema,
  latLngSchema,
  nameSchema,
  objectIdSchema,
  paginationSchema,
  paginationWithSearchSchema,
  savedLocationContactSchema,
} from '@hero/schema';
import { API } from '..';
import { z } from 'zod';

const schema = z.object({
  name: nameSchema.describe(
    "Display name (e.g. 'Riyadh Warehouse', 'Jeddah Branch', 'Factory 092').",
  ),

  location: latLngSchema.describe(
    "Latitude / longitude of the pin. Resolve from free-text via `resolve_location_point` or pick from `Region`'s `center` when the user named a city.",
  ),

  clientId: objectIdSchema
    .optional()
    .describe(
      "Client (`Client._id`) this saved location belongs to. Omit to create as the caller's own branch.",
    ),

  locationUrl: z
    .string()
    .url()
    .optional()
    .describe(
      'Optional Google Maps share URL. Useful when the user cannot drop the exact pin — the backend `resolveGoogleUrl` endpoint can extract `{ lat, lng, name? }`.',
    ),

  isAccurate: z
    .boolean()
    .optional()
    .describe(
      'Whether the pin is exact. `false` = approximate (e.g. "Sulay neighborhood"). Defaults to `true` when omitted.',
    ),

  contacts: z
    .array(savedLocationContactSchema.pick({ _id: true }))
    .optional()
    .describe(
      'Contacts assigned to this location. Each entry is a `Contact._id` reference. Omit / empty array → no contacts.',
    ),
});

// * get all api
export const savedLocationGetAllSchemaAPI = paginationWithSearchSchema.extend({
  clientId: objectIdSchema
    .optional()
    .describe(
      "Filter saved locations by Client (`Client._id`). When omitted, returns locations across all clients. This is the third-party Client (counterparty), NOT the caller's own Org.",
    ),
  contactId: objectIdSchema
    .optional()
    .describe(
      'Filter saved locations that have this contact assigned (`Contact._id` on `SavedLocation.contacts[]`).',
    ),
  isOwnBranch: booleanSchema
    .optional()
    .describe(
      "When true, narrow to the caller's own branches (not tied to a third-party Client). When false, exclude own branches. Omit for both.",
    ),
});
export type SavedLocationGetAllAPI = API<
  z.infer<typeof savedLocationGetAllSchemaAPI>,
  SavedLocation[]
>;

//* search api
export const savedLocationSearchSchemaAPI = paginationSchema.extend({
  search: nameSchema.describe(
    'Substring to search for (matches saved-place name or Client name).',
  ),
});
export type SavedLocationSearchAPI = API<
  z.infer<typeof savedLocationSearchSchemaAPI>,
  SavedLocation[]
>;

//* create api
export const savedLocationCreateAPISchema = schema;
export type SavedLocationCreateAPI = API<
  z.infer<typeof savedLocationCreateAPISchema>,
  SavedLocation
>;

//* update api
export const savedLocationUpdateAPISchema = schema.partial().extend({
  _id: objectIdSchema.describe('`SavedLocation._id` to update.'),
});
export type SavedLocationUpdateAPI = API<
  z.infer<typeof savedLocationUpdateAPISchema>,
  SavedLocation
>;

// * get one api
export const savedLocationGetOneSchemaAPI = savedLocationUpdateAPISchema.pick({ _id: true });
export type SavedLocationGetOneAPI = API<
  z.infer<typeof savedLocationGetOneSchemaAPI>,
  SavedLocation
>;

// * delete api
export const savedLocationDeleteAPISchema = savedLocationUpdateAPISchema.pick({ _id: true });
export type SavedLocationDeleteAPI = API<z.infer<typeof savedLocationDeleteAPISchema>, undefined>;

//* resolve google maps url
/**
 * Resolve a Google Maps share URL (long or short `maps.app.goo.gl`
 * link) to `{ lat, lng, name?, address?, placeId? }` for prefilling
 * the location picker. The backend follows short-link redirects and
 * parses the long URL for embedded coordinates; falls back to the
 * Geocoding API when only a place name is available.
 */
export const savedLocationResolveGoogleUrlSchemaAPI = z.object({
  /** The Google Maps URL to resolve. Long `google.com/maps/…` or short `maps.app.goo.gl/…` / `goo.gl/maps/…`. */
  url: z.string().url(),
});
export type SavedLocationResolveGoogleUrlData = {
  lat: number;
  lng: number;
  name?: string;
  address?: string;
  placeId?: string;
};
export type SavedLocationResolveGoogleUrlAPI = API<
  z.infer<typeof savedLocationResolveGoogleUrlSchemaAPI>,
  SavedLocationResolveGoogleUrlData
>;

// ===== Admin Apis ======

export const savedLocationGetAllAdminAPISchema = savedLocationGetAllSchemaAPI.extend({
  /** this is the organization id. This will be used by Super Admin only. */
  orgId: objectIdSchema.optional(),
});
export type SavedLocationGetAllAdminAPI = API<
  z.infer<typeof savedLocationGetAllAdminAPISchema>,
  SavedLocation[]
>;
export const savedLocationCreateAdminAPISchema = savedLocationCreateAPISchema.extend({
  /** this is the organization id. This will be used by Super Admin only. */
  orgId: objectIdSchema,
  isOwnBranch: z.boolean(),
});
export type SavedLocationCreateAdminAPI = API<
  z.infer<typeof savedLocationCreateAdminAPISchema>,
  SavedLocation
>;
